import * as React from 'react';
import { cn } from '@/lib/utils';
import { useDismissableOverlay } from '@/lib/use-dismissable-overlay';

// Minimal hand-rolled modal — no Radix, no shadcn install. Body scroll is
// frozen while open and Esc/backdrop-click close, which is enough for our
// confirmation prompts and one-shot secret reveals.

interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export function Dialog({ open, onClose, title, description, children, footer, className }: DialogProps) {
  useDismissableOverlay(open, onClose);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        className={cn(
          'w-full max-w-md rounded-lg border bg-background text-foreground shadow-lg',
          className,
        )}
      >
        {(title || description) && (
          <div className="space-y-1.5 border-b px-6 py-4">
            {title && <h2 className="text-base font-semibold tracking-tight">{title}</h2>}
            {description && <p className="text-sm text-muted-foreground">{description}</p>}
          </div>
        )}
        {children && <div className="px-6 py-4 text-sm">{children}</div>}
        {footer && (
          <div className="flex items-center justify-end gap-2 border-t bg-muted/30 px-6 py-3">{footer}</div>
        )}
      </div>
    </div>
  );
}
